import {Injectable, InjectionToken, Injector, ValueProvider} from '@angular/core';
import {ComponentPortal, ComponentType} from "@angular/cdk/portal";
import {Overlay, OverlayRef} from "@angular/cdk/overlay";
import {DialogComponent, DialogInput} from "../../views/service-parts/dialog/dialog.component";
import {Subscription} from "rxjs";
import {NavigationStart, Router} from "@angular/router";

export interface InformationInput {
  title?: string;
  content: string;
  submitText?: string;
}

export interface AlertInput {
  title?: string;
  content: string;
  submitText?: string;
}

export interface ConfirmInput {
  title?: string;
  content: string;
  submitText?: string;
  cancelText?: string;
  isImportant?: boolean;
}

export const APP_DIALOG_DATA = new InjectionToken<any>('APP_DIALOG_DATA');

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  ret: any = false;

  globalOverlay: OverlayRef = this.overlay.create({
    hasBackdrop: true,
    positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
    maxWidth: '90vw',
    maxHeight: '90vh',
    panelClass: 'dialog-panel',
  });

  customGlobalOverlay: OverlayRef = this.overlay.create({
    hasBackdrop: true,
    positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
    maxWidth: '90vw',
    maxHeight: '90vh',
    panelClass: 'dialog-panel',
  });

  constructor(
    private overlay: Overlay,
    private readonly injector: Injector,
    private router: Router,
  ) {
  }

  async information(informationInput: InformationInput): Promise<boolean> {
    const dialogInput: DialogInput = {
      title: informationInput.title ?? '確認',
      content: informationInput.content,
      submitText: informationInput.submitText ?? 'OK',
      cancelText: null,
    }
    return this.show(dialogInput);
  }

  async alert(alertInput: AlertInput): Promise<boolean> {
    const dialogInput: DialogInput = {
      title: alertInput.title ?? '確認',
      content: alertInput.content,
      submitText: alertInput.submitText ?? 'OK',
      cancelText: null,
      isError: true,
    }
    return this.show(dialogInput);
  }

  async confirm(confirmInput: ConfirmInput): Promise<boolean> {
    const dialogInput: DialogInput = {
      title: confirmInput.title ?? '確認',
      content: confirmInput.content,
      submitText: confirmInput.submitText ?? 'OK',
      cancelText: confirmInput.cancelText ?? 'キャンセル',
      isImportant: !!confirmInput.isImportant,
    }
    return this.show(dialogInput);
  }

  async customComponent<T>(customComponent: ComponentType<any>, customData?: Object): Promise<T> {
    return this.show(null, customComponent, customData);
  }

  private async show<T>(data: DialogInput | null, customComponent?: ComponentType<any>, customData?: Object): Promise<T> {
    const globalOverlay = !customComponent ? this.globalOverlay : this.customGlobalOverlay;
    const injectData = customData ?? data;

    if (!globalOverlay.hasAttached()) {
      return new Promise((resolve) => {
        // dialogを表示している状態でブラウザバックが行われた場合はfalseを返しつつdialogを閉じる
        const routerEventSubscription: Subscription = this.router.events.subscribe((event) => {
          if (event instanceof NavigationStart) {
            this.hide(false);
            this.hide(false, true);  // customGlobalOverlay側のhide指定
          }
        });

        // globalOverlayがdetachされた際に呼ばれるコールバック
        const overlayEventSubscription: Subscription = globalOverlay.detachments().subscribe(() => {
          overlayEventSubscription.unsubscribe();
          routerEventSubscription.unsubscribe();
          resolve(this.ret);
        });

        let injector: Injector | null = null;
        if (!customComponent || (customComponent && injectData)) {
          // DialogComponentにパラメータを渡すための指定
          injector= Injector.create({
            parent: this.injector,
            providers: [
              {provide: APP_DIALOG_DATA, useValue: injectData} as ValueProvider,
            ],
          });
        }

        const component = customComponent ?? DialogComponent;

        const componentPortal: ComponentPortal<any> = new ComponentPortal(component, null, injector);
        const globalOverLaySubscription: Subscription = globalOverlay.attachments().subscribe(() => {
          globalOverLaySubscription.unsubscribe();

          // わずかに待ちを入れないと表示時のアニメーションが行われないためsetTimeoutで待ちを入れる
          setTimeout(() => {
            globalOverlay.addPanelClass('show');
          }, 0);
        });

        globalOverlay.attach(componentPortal);
      });
    } else {
      return this.ret;
    }
  }

  hide<T>(ret: T, isCustomComponent: boolean = false): void {
    const globalOverlay = !isCustomComponent ? this.globalOverlay : this.customGlobalOverlay;

    if (globalOverlay.hasAttached()) {
      this.ret = ret;
      globalOverlay.removePanelClass('show');

      // 非表示CSSアニメーションが完了した後にdetachして要素を削除する
      setTimeout(() => {
        globalOverlay.detach();
      }, 100);
    }
  }
}
